<template>
    <PanelPage>
        <template v-slot:title>
            <SmartBreadcrumbs
                :pages="[
                    {
                        name: $tc('messages.activities', 2),
                        href: meta.links.index,
                    },
                    {
                        name: activity.title,
                        href: meta.links.show,
                    },
                    {
                        name: $tc('messages.questions', 2),
                        href: null,
                    },
                ]"
            />
        </template>

        <div class="md:flex justify-between">
            <div>
                <aside class="justify-self-center">
                    <nav
                        class="inline-flex items-center justify-center p-1 space-x-1 overflow-hidden text-sm text-gray-600 bg-gray-100 rounded-xl"
                    >
                        <template v-for="link in meta.menu.links" :key="link.url">
                            <template v-if="link.selected">
                                <AppButton :href="link.url" element="inertia-link">
                                    {{ link.display_name }}
                                </AppButton>
                            </template>
                            <template v-else>
                                <InertiaLink
                                    :href="link.url"
                                    class="inline-flex items-center h-8 px-5 font-medium transition rounded-lg hover:text-gray-800 focus:text-indigo-600 focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:ring-inset"
                                >
                                    {{ link.display_name }}
                                </InertiaLink>
                            </template>
                        </template>
                    </nav>
                </aside>
            </div>
            <div>
                <ActivityQuestionsEvaluationCriteriaPanel
                    v-if="selectedQuestions.length"
                    :evaluation-criteria="evaluationCriteria.items"
                    :questions-ids="selectedQuestions"
                    :endpoint="evaluationCriteria.endpoint"
                    @finished="selectedQuestions = []"
                    :evaluation-criteria-link="meta.links.evaluation_criteria_create"
                />
            </div>
        </div>

        <div>
            <label for="">
                <input
                    type="checkbox"
                    class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                    v-model="showMissingOnly"
                />

                {{ $t('messages.show_only_missing') }}
            </label>
        </div>

        <div class="flex flex-col mt-5">
            <div class="-my-2 py-2 overflow-x-auto sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
                <div
                    class="align-middle inline-block min-w-full shadow overflow-hidden sm:rounded-lg border-b border-gray-200"
                >
                    <table class="table-fixed min-w-full divide-y divide-gray-200">
                        <thead>
                            <tr>
                                <th scope="col" class="relative w-12 px-6 sm:w-16 sm:px-8 bg-gray-50">
                                    <SelectAllNoneButtons
                                        :max-count="questions.length"
                                        :selected-count="selectedQuestions.length"
                                        @select-all="selectAllQuestions()"
                                        @select-none="selectedQuestions = []"
                                        class="flex"
                                    />
                                </th>
                                <th
                                    class="w-10 max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    <div>{{ $t('messages.title') }}</div>
                                    {{ $t('messages.description') }}
                                </th>
                                <th
                                    class="px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $t('messages.difficulty') }}
                                </th>
                                <th
                                    class="px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $tc('messages.evaluation_criteria', 2) }}
                                </th>
                            </tr>
                        </thead>

                        <tbody>
                            <tr
                                v-for="(question, sessionIndex) in filteredQuestions"
                                :class="sessionIndex % 2 === 0 ? 'bg-white' : 'bg-gray-100'"
                                :key="sessionIndex"
                            >
                                <td class="relative w-12 px-6 sm:w-16 sm:px-8">
                                    <div
                                        v-if="selectedQuestions.includes(question.id)"
                                        class="absolute inset-y-0 left-0 w-0.5 bg-indigo-600"
                                    />
                                    <input
                                        type="checkbox"
                                        class="absolute left-4 top-1/2 -mt-2 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 sm:left-6"
                                        :value="question.id"
                                        v-model="selectedQuestions"
                                    />
                                </td>
                                <td class="max-w-md px-6 py-4 text-sm leading-5 text-gray-900">
                                    <div class="md:w-64 max-w-md">
                                        <span>{{ question.title }}</span>
                                    </div>
                                    <div
                                        class="max-w-xs whitespace-pre-line text-gray-600 text-xs"
                                        v-if="question.description"
                                    >
                                        <QuestionDescriptionDisplay :question="question" />
                                    </div>
                                </td>
                                <td class="truncate px-6 py-4 text-sm leading-5 text-gray-900">
                                    <SmallNumberRangeIndicator :max="5" :value="question.difficulty" />
                                </td>
                                <td class="truncate px-6 py-4 text-sm leading-5 text-gray-900">
                                    <ul>
                                        <li
                                            v-for="evaluationCriterion in question.evaluationCriteria"
                                            :key="evaluationCriterion.id"
                                        >
                                            <BadgePill color="indigo">
                                                {{ evaluationCriterion.title }}
                                            </BadgePill>
                                        </li>
                                    </ul>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    </PanelPage>
</template>

<script>
import Layout from '@/Shared/Layout.vue';
import PanelPage from '@/Shared/PanelPage.vue';
import SelectAllNoneButtons from '@/components/SelectAllNoneButtons.vue';
import SmallNumberRangeIndicator from '@/components/SmallNumberRangeIndicator.vue';
import ActivityQuestionsEvaluationCriteriaPanel from '@/components/pageSpecific/ActivityQuestionsEvaluationCriteriaPanel.vue';
import BadgePill from '@/components/BadgePill.vue';
import AppButton from '@/components/AppButton.vue';
import QuestionDescriptionDisplay from '@/components/Quiz/QuestionDescriptionDisplay.vue';

export default {
    components: {
        QuestionDescriptionDisplay,
        PanelPage,
        SmallNumberRangeIndicator,
        SelectAllNoneButtons,
        ActivityQuestionsEvaluationCriteriaPanel,
        BadgePill,
        AppButton,
    },
    layout: Layout,
    name: 'Edit',
    props: ['meta', 'questions', 'activity', 'filters', 'evaluationCriteria'],
    data() {
        return {
            selectedQuestions: [],
            showMissingOnly: false,
        };
    },
    methods: {
        selectAllQuestions() {
            if (this.showMissingOnly) {
                this.selectedQuestions = this.filteredQuestions.map((q) => q.id);
            } else {
                this.selectedQuestions = this.questions.map((q) => q.id);
            }
        },
    },
    computed: {
        filteredQuestions() {
            if (this.showMissingOnly) {
                return this.questions.filter((q) => q.evaluationCriteria.length === 0);
            }

            return this.questions;
        },
    },
};
</script>
